import { ShieldCheck, Users, BadgeCheck, MapPin } from 'lucide-react';
import { KineticLines, FadeUp, Eyebrow } from '../components/motion';
import { CTASection } from '../components/CTASection';
import { IMAGES, STATS } from '../data/content';

const VALUES = [
  {
    icon: ShieldCheck,
    title: 'Güven Önce Gelir',
    desc: 'Tüm taşımalarımız sigorta kapsamındadır. Eşyanız bizim için emanet, emanete sadakat esastır.',
  },
  {
    icon: Users,
    title: 'Kadrolu Uzman Ekip',
    desc: 'Günlük personel değil; yıllardır birlikte çalışan, eğitimli ve deneyimli kendi ekibimiz.',
  },
  {
    icon: BadgeCheck,
    title: 'Sözleşmeli & Net Fiyat',
    desc: 'Keşif sonrası verdiğimiz fiyat kesindir. Taşıma günü sürpriz ücret çıkarmayız.',
  },
  {
    icon: MapPin,
    title: 'İnegöl Merkez, 81 İl Hat',
    desc: 'Bursa İnegöl merkezli firmamızla Türkiye\u2019nin her noktasına düzenli sefer planlıyoruz.',
  },
];

const Hero = () => (
  <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
    <div className="absolute inset-0">
      <img src={IMAGES.about} alt="Vira Nakliyat ekibi" className="w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#060B19]/70 via-[#060B19]/85 to-[#060B19]" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 md:px-10">
      <FadeUp delay={0.1}>
        <Eyebrow>Hakkımızda</Eyebrow>
      </FadeUp>
      <KineticLines
        className="mt-6 font-display font-medium uppercase tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl"
        lines={[
          <span key="1" className="block">Eşyanız değil,</span>,
          <span key="2" className="block text-[#FF5C00]">hatıralarınızı taşıyoruz</span>,
        ]}
      />
    </div>
  </section>
);

const Story = () => (
  <section data-testid="about-story" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
      <FadeUp className="relative">
        <div className="rounded-2xl overflow-hidden border border-[#1E293B]">
          <img src={IMAGES.movers} alt="Taşıma ekibimiz çalışırken" className="w-full h-[420px] md:h-[520px] object-cover" />
        </div>
        <div className="absolute -bottom-8 -right-4 md:-right-8 w-44 md:w-64 rounded-2xl overflow-hidden border-4 border-[#060B19] shadow-2xl">
          <img src={IMAGES.interior} alt="Yeni evde kurulum" className="w-full h-36 md:h-48 object-cover" />
        </div>
      </FadeUp>

      <FadeUp delay={0.15}>
        <Eyebrow>Hikâyemiz</Eyebrow>
        <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl leading-tight">
          İnegöl&apos;ün sokaklarından Türkiye&apos;nin <span className="text-[#FF5C00]">dört bir yanına</span>
        </h2>
        <div className="mt-7 space-y-5 text-[#A0ABC0] leading-relaxed">
          <p>
            Vira Nakliyat, İnegöl&apos;de evden eve taşımacılıkla başlayan yolculuğunu bugün
            Türkiye&apos;nin 81 iline uzanan bir hizmet ağına dönüştürmüştür. Biliyoruz ki bir evi
            taşımak, yalnızca eşya taşımak değildir; bir ailenin anılarını, emeğini ve yeni bir
            başlangıcın heyecanını taşımaktır.
          </p>
          <p>
            Bu bilinçle her taşımayı kendi evimizi taşıyormuş gibi planlıyoruz: ücretsiz keşif,
            net fiyat, sigortalı taşıma, asansörlü yükleme ve yerinde kurulum. Binlerce ailenin
            yeni yuvasına ilk adımını bizimle atması, en büyük referansımızdır.
          </p>
        </div>
      </FadeUp>
    </div>
  </section>
);

const Values = () => (
  <section data-testid="about-values" className="py-24 md:py-32 bg-[#0A1128] border-y border-[#1E293B]">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <FadeUp className="mb-14">
        <Eyebrow>Değerlerimiz</Eyebrow>
        <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl leading-tight">
          Bizi biz yapan <span className="text-[#FF5C00]">ilkeler</span>
        </h2>
      </FadeUp>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {VALUES.map((v, i) => (
          <FadeUp key={v.title} delay={i * 0.08}>
            <div
              data-testid={`value-card-${i}`}
              className="card-hover group h-full bg-[#060B19] border border-[#1E293B] rounded-2xl p-9"
            >
              <v.icon size={26} className="text-[#FF5C00] mb-6" />
              <h3 className="font-display text-xl md:text-2xl tracking-tight mb-3">{v.title}</h3>
              <p className="text-[#A0ABC0] text-sm leading-relaxed">{v.desc}</p>
            </div>
          </FadeUp>
        ))}
      </div>

      <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-10">
        {STATS.map((s, i) => (
          <FadeUp key={s.label} delay={i * 0.08} className="text-center">
            <div className="font-display font-semibold text-4xl md:text-5xl tracking-tighter">
              {s.value}
            </div>
            <div className="mt-2 text-[11px] uppercase tracking-[0.25em] text-[#FF5C00]">
              {s.label}
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

export default function HakkimizdaPage() {
  return (
    <main data-testid="about-page">
      <Hero />
      <Story />
      <Values />
      <CTASection />
    </main>
  );
}
